import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, BarChart, FileText, Shield, Globe, Calendar, FileSearch, Package, Settings, AlertTriangle, Trash2, Loader2 } from 'lucide-react';
import { TypeToConfirmDialog } from '@/components/ui/type-to-confirm-dialog';
import { PageManager } from '../components/page-manager';
import { DeepCrawlModal } from '../components/deep-crawl/DeepCrawlModal';
import { CatalogView } from '../components/catalog/CatalogView';
import { API_URL } from '../config/api';
import { toast } from '../components/ui/use-toast';
import { useAuth } from '../context/auth-context';
import { FeatureToggles } from '../components/site-settings/feature-toggles';
import { EnhancedVoiceSettings } from '../components/site-settings/enhanced-voice-settings';
import { ChatbotSettings } from '../components/site-settings/chatbot-settings';
import { NaturalLanguageSearchSettings } from '../components/site-settings/natural-language-search-settings';

interface Site {
  id: string;
  _id: string;
  name: string;
  domain?: string;
  url: string;
  status: 'active' | 'pending' | 'inactive';
  owner_email?: string;
  analytics: boolean;
  chatbot: boolean;
  created: string;
  updated?: string;
}

export default function SiteDetails() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deepCrawlModalOpen, setDeepCrawlModalOpen] = useState(false);
  const [deleteAllDataDialogOpen, setDeleteAllDataDialogOpen] = useState(false);
  const [deletingAllData, setDeletingAllData] = useState(false);

  const isAdmin = user?.role === 'super_admin';
  
  // Determine the correct back navigation path
  const getBackPath = () => {
    // Check if we came from admin sites page
    if (location.state?.from === '/admin/sites' || user?.role === 'super_admin') {
      return '/admin/sites';
    }
    // Default to my-sites for regular users
    return '/my-sites';
  };

  useEffect(() => {
    if (siteId && siteId !== 'undefined') {
      fetchSiteDetails();
    } else if (siteId === 'undefined' || !siteId) {
      // Redirect to My Sites page if site ID is undefined
      console.warn('[SiteDetails] Invalid site ID:', siteId);
      navigate('/my-sites');
    }
  }, [siteId, navigate]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/sites/${siteId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch site details');
      }

      const data = await response.json();
      setSite(data);
    } catch (err) {
      setError('Failed to load site details');
      console.error('Error fetching site:', err);
      toast({
        title: 'Error',
        description: 'Failed to load site details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllSiteData = async () => {
    if (!siteId || !site?.name) return;

    setDeletingAllData(true);
    try {
      const response = await fetch(`${API_URL}/sites/${siteId}/all-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          confirmationName: site.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete site data');
      }

      toast({
        title: 'Success',
        description: `Deleted ${data.totalDeleted} records from site "${site.name}"`,
      });

      setDeleteAllDataDialogOpen(false);
      fetchSiteDetails(); // Refresh the page
    } catch (error) {
      console.error('Error deleting site data:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete site data',
        variant: 'destructive',
      });
    } finally {
      setDeletingAllData(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Site not found'}</AlertDescription>
        </Alert>
        <Button
          onClick={() => navigate(getBackPath())}
          className="mt-4"
          variant="outline"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Sites
        </Button>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      pending: 'secondary',
      inactive: 'destructive',
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          onClick={() => navigate(getBackPath())}
          variant="ghost"
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Sites
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{site.name || 'Unnamed Site'}</h1>
            <p className="text-muted-foreground mt-1">{site.domain || site.url}</p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(site.status)}
            {user?.role === 'super_admin' && (
              <Button
                variant="outline"
                onClick={() => setDeepCrawlModalOpen(true)}
              >
                <FileSearch className="h-4 w-4 mr-2" />
                Deep Crawl
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Site Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Owner Email</p>
              <p className="font-medium">{site.owner_email || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-medium flex items-center">
                <Calendar className="mr-1 h-3 w-3" />
                {new Date(site.created).toLocaleDateString()}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Features</p>
              <div className="flex gap-1">
                {site.analytics && <Badge variant="outline" className="text-xs">Analytics</Badge>}
                {site.chatbot && <Badge variant="outline" className="text-xs">Chatbot</Badge>}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Site URL</p>
              <a 
                href={site.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline flex items-center"
              >
                <Globe className="mr-1 h-3 w-3" />
                Visit Site
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue={location.state?.defaultTab || "pages"} className="space-y-4">
        <TabsList>
          <TabsTrigger value="pages" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Pages
          </TabsTrigger>
          <TabsTrigger value="catalog" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Catalog
          </TabsTrigger>
          <TabsTrigger value="topics" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Topics
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pages" className="space-y-4">
          <PageManager siteId={siteId!} />
        </TabsContent>

        <TabsContent value="catalog" className="space-y-4">
          <CatalogView siteId={siteId!} />
        </TabsContent>

        <TabsContent value="topics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Topics Management</CardTitle>
              <CardDescription>
                Manage topics and sectors associated with this site
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  Topics management is coming soon. This will allow you to manage the topics and sectors associated with this site.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4">
            {/* Chatbot Analytics Card */}
            {site.chatbot && (
              <Card>
                <CardHeader>
                  <CardTitle>Chatbot Analytics</CardTitle>
                  <CardDescription>
                    Comprehensive analysis of chatbot conversations and performance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => navigate(`/chatbot-analytics/${siteId}`)}
                    className="w-full sm:w-auto"
                  >
                    <BarChart className="h-4 w-4 mr-2" />
                    View Chatbot Analytics
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* General Analytics Card */}
            <Card>
              <CardHeader>
                <CardTitle>Site Analytics</CardTitle>
                <CardDescription>
                  View visitor statistics and performance metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert>
                  <AlertDescription>
                    General analytics dashboard is coming soon. This will show visitor statistics, page performance, and user behavior metrics.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Configure security settings for this site
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  Security configuration is coming soon. This will allow you to manage SSL certificates, access controls, and security headers.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <div className="space-y-6">
            {/* Feature Toggles */}
            <FeatureToggles siteId={siteId!} />

            {/* Chatbot Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Chatbot Configuration</CardTitle>
                <CardDescription>
                  Customize the appearance and behavior of your chatbot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChatbotSettings siteId={siteId!} />
              </CardContent>
            </Card>

            {/* Voice Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Voice Assistant Configuration</CardTitle>
                <CardDescription>
                  Configure voice interaction settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EnhancedVoiceSettings siteId={siteId!} />
              </CardContent>
            </Card>

            {/* Natural Language Search Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Natural Language Search</CardTitle>
                <CardDescription>
                  Configure AI-powered search for your site
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NaturalLanguageSearchSettings siteId={siteId!} />
              </CardContent>
            </Card>

            {/* Danger Zone - Super Admin Only */}
            {isAdmin && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-5 w-5" />
                    Danger Zone
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    These actions are destructive and cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                    <div className="flex-1">
                      <h3 className="font-medium text-red-800">Delete All Site Data</h3>
                      <p className="text-sm text-red-600 mt-1">
                        Permanently delete all data associated with this site including:
                      </p>
                      <ul className="list-disc list-inside text-sm text-red-600 mt-2 space-y-1">
                        <li>Product feed items and knowledge base entries</li>
                        <li>Site categories and category mappings</li>
                        <li>AEO onboarding data and question wheels</li>
                        <li>Page extractions and embeddings</li>
                        <li>Chatbot conversations and voice sessions</li>
                      </ul>
                      <p className="text-sm text-red-700 font-medium mt-3">
                        The site record will be preserved. Only site data will be deleted.
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteAllDataDialogOpen(true)}
                      className="mt-4"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All Site Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Type to Confirm Dialog for Site Data Deletion */}
      <TypeToConfirmDialog
        open={deleteAllDataDialogOpen}
        onOpenChange={setDeleteAllDataDialogOpen}
        title="Delete All Site Data"
        description={
          <div>
            <p className="mb-3">
              You are about to permanently delete <strong>all data</strong> for site "{site?.name}".
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The site record will be preserved, but all associated data will be permanently deleted.
            </p>
          </div>
        }
        confirmationText={site?.name || ''}
        confirmationLabel="Type the site name to confirm"
        onConfirm={handleDeleteAllSiteData}
        loading={deletingAllData}
        destructiveAction="Delete All Data"
      />

      {/* Deep Crawl Modal for Super Admins */}
      {site && token && (
        <DeepCrawlModal
          open={deepCrawlModalOpen}
          onOpenChange={setDeepCrawlModalOpen}
          siteId={site.id || site._id}
          siteName={site.name || site.domain || site.url}
          siteUrl={site.url}
          token={token}
          onCrawlStarted={() => {
            // Optionally refresh the page data after crawl starts
            fetchSiteDetails();
          }}
        />
      )}

    </div>
  );
}